import { useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './ImportPage.module.css';

interface FieldOption {
  value: string;
  label: string;
}

interface ImportError {
  row: number;
  error: string;
  company?: string;
}

type Step = 'upload' | 'preview' | 'progress' | 'complete';

const LEAD_FIELDS: FieldOption[] = [
  { value: '', label: '-- Saltar --' },
  { value: 'companyName', label: 'Empresa (companyName)' },
  { value: 'contactName', label: 'Contacto (contactName)' },
  { value: 'contactTitle', label: 'Cargo (contactTitle)' },
  { value: 'contactEmail', label: 'Email (contactEmail)' },
  { value: 'contactPhone', label: 'Teléfono (contactPhone)' },
  { value: 'websiteUrl', label: 'Website (websiteUrl)' },
  { value: 'sector', label: 'Sector' },
  { value: 'subsector', label: 'Subsector' },
  { value: 'tier', label: 'Tier (T1/T2/T3)' },
  { value: 'region', label: 'Región' },
  { value: 'fitScore', label: 'Fit Score (1-5)' },
  { value: 'readinessScore', label: 'Readiness (Alta/Media/Baja)' },
  { value: 'source', label: 'Fuente' },
  { value: 'painPoint', label: 'Pain Point' },
  { value: 'commercialAngle', label: 'Ángulo Comercial' },
  { value: 'uspVerified', label: 'USP Verificado' },
  { value: 'productStar', label: 'Producto Estrella' },
  { value: 'notes', label: 'Notas' },
];

const HEADER_ALIASES: Record<string, string> = {
  empresa: 'companyName',
  company: 'companyName',
  'nombre empresa': 'companyName',
  'company name': 'companyName',
  contacto: 'contactName',
  contact: 'contactName',
  'nombre contacto': 'contactName',
  'contact name': 'contactName',
  nombre: 'contactName',
  cargo: 'contactTitle',
  title: 'contactTitle',
  'job title': 'contactTitle',
  email: 'contactEmail',
  correo: 'contactEmail',
  'e-mail': 'contactEmail',
  mail: 'contactEmail',
  telefono: 'contactPhone',
  phone: 'contactPhone',
  tel: 'contactPhone',
  website: 'websiteUrl',
  web: 'websiteUrl',
  url: 'websiteUrl',
  sitio: 'websiteUrl',
  sector: 'sector',
  subsector: 'subsector',
  tier: 'tier',
  region: 'region',
  'fit score': 'fitScore',
  fitscore: 'fitScore',
  readiness: 'readinessScore',
  readinessscore: 'readinessScore',
  fuente: 'source',
  source: 'source',
  'pain point': 'painPoint',
  painpoint: 'painPoint',
  dolor: 'painPoint',
  angulo: 'commercialAngle',
  'commercial angle': 'commercialAngle',
  usp: 'uspVerified',
  uspverified: 'uspVerified',
  producto: 'productStar',
  'product star': 'productStar',
  notas: 'notes',
  notes: 'notes',
};

function parseCSVText(text: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(current.trim());
        current = '';
      } else if (ch === '\r') {
        continue;
      } else if (ch === '\n') {
        row.push(current.trim());
        current = '';
        if (row.length > 0 && row.some((c) => c !== '')) {
          rows.push(row);
        }
        row = [];
      } else {
        current += ch;
      }
    }
  }

  row.push(current.trim());
  if (row.length > 0 && row.some((c) => c !== '')) {
    rows.push(row);
  }

  return rows;
}

function autoDetectMapping(headers: string[]): Record<number, string> {
  const mapping: Record<number, string> = {};
  headers.forEach((h, idx) => {
    const key = h.toLowerCase().trim();
    if (HEADER_ALIASES[key]) {
      mapping[idx] = HEADER_ALIASES[key];
    }
  });
  return mapping;
}

interface UiState {
  step: Step;
  dragOver: boolean;
  message: { text: string; type: 'success' | 'error' } | null;
  defaultSource: string;
  defaultStage: string;
}

interface CsvState {
  text: string;
  headers: string[];
  rows: string[][];
  fieldMap: Record<number, string>;
}

interface ProgressState {
  current: number;
  total: number;
  success: number;
  errors: number;
  errorList: ImportError[];
  summarySuccess: number;
  summaryErrors: number;
}

function useImport() {
  const [ui, setUi] = useState<UiState>({
    step: 'upload',
    dragOver: false,
    message: null,
    defaultSource: 'manual',
    defaultStage: 'new',
  });
  const [csv, setCsv] = useState<CsvState>({
    text: '',
    headers: [],
    rows: [],
    fieldMap: {},
  });
  const [progress, setProgress] = useState<ProgressState>({
    current: 0,
    total: 0,
    success: 0,
    errors: 0,
    errorList: [],
    summarySuccess: 0,
    summaryErrors: 0,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setUi({
      step: 'upload',
      dragOver: false,
      message: null,
      defaultSource: 'manual',
      defaultStage: 'new',
    });
    setCsv({ text: '', headers: [], rows: [], fieldMap: {} });
    setProgress({
      current: 0,
      total: 0,
      success: 0,
      errors: 0,
      errorList: [],
      summarySuccess: 0,
      summaryErrors: 0,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = typeof ev.target?.result === 'string' ? ev.target.result : '';
      setCsv((prev) => ({ ...prev, text: result }));
      parseCSVFromText(result);
    };
    reader.readAsText(file);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      handleFile(file);
    },
    [handleFile]
  );

  const parseCSVFromText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      setUi((prev) => ({
        ...prev,
        message: { text: 'Pega o sube un CSV primero', type: 'error' },
      }));
      return;
    }

    const rows = parseCSVText(trimmed);
    if (rows.length < 2) {
      setUi((prev) => ({
        ...prev,
        message: {
          text: 'El CSV debe tener al menos una fila de encabezados y una fila de datos',
          type: 'error',
        },
      }));
      return;
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);

    setCsv({ text: trimmed, headers, rows: dataRows, fieldMap: autoDetectMapping(headers) });
    setUi((prev) => ({ ...prev, step: 'preview', message: null }));
  }, []);

  const parseCSV = useCallback(() => {
    parseCSVFromText(csv.text);
  }, [csv.text, parseCSVFromText]);

  const updateFieldMap = useCallback((idx: number, value: string) => {
    setCsv((prev) => {
      const next = { ...prev.fieldMap };
      if (value) {
        next[idx] = value;
      } else {
        delete next[idx];
      }
      return { ...prev, fieldMap: next };
    });
  }, []);

  const startImport = useCallback(async () => {
    const hasCompany = Object.values(csv.fieldMap).some((v) => v === 'companyName');
    if (!hasCompany) {
      setUi((prev) => ({
        ...prev,
        message: {
          text: 'Debes mapear al menos la columna de Empresa (companyName)',
          type: 'error',
        },
      }));
      return;
    }

    setUi((prev) => ({ ...prev, step: 'progress' }));
    setProgress((prev) => ({
      ...prev,
      current: 0,
      total: csv.rows.length,
      success: 0,
      errors: 0,
      errorList: [],
    }));

    let success = 0;
    let errors = 0;
    const currentErrorList: ImportError[] = [];

    async function processNext(i: number): Promise<void> {
      if (i >= csv.rows.length) return;
      const row = csv.rows[i];
      const lead: Record<string, string> = {
        source: ui.defaultSource,
        stage: ui.defaultStage,
      };

      Object.keys(csv.fieldMap).forEach((colIdxStr) => {
        const colIdx = parseInt(colIdxStr, 10);
        const field = csv.fieldMap[colIdx];
        const val = (row[colIdx] || '').trim();
        if (val) {
          lead[field] = val;
        }
      });

      if (!lead.companyName) {
        errors++;
        currentErrorList.push({ row: i + 2, error: 'Empresa vacía' });
        setProgress((prev) => ({
          ...prev,
          current: i + 1,
          success,
          errors,
        }));
        return processNext(i + 1);
      }

      try {
        const res = await fetch('/api/leads', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lead),
        });

        if (res.ok) {
          success++;
        } else {
          errors++;
          let errText = 'Error desconocido';
          try {
            const errData = await res.json();
            if (errData && typeof errData === 'object' && 'error' in errData) {
              errText = String((errData as { error: string }).error);
            }
          } catch {
            // ignore
          }
          currentErrorList.push({ row: i + 2, error: errText, company: lead.companyName });
        }
      } catch (e) {
        errors++;
        const errMsg = e instanceof Error ? e.message : 'Error de red';
        currentErrorList.push({ row: i + 2, error: errMsg, company: lead.companyName });
      }

      setProgress((prev) => ({
        ...prev,
        current: i + 1,
        success,
        errors,
      }));
      return processNext(i + 1);
    }

    await processNext(0);

    setProgress((prev) => ({ ...prev, errorList: currentErrorList }));

    setTimeout(() => {
      setProgress((prev) => ({
        ...prev,
        summarySuccess: success,
        summaryErrors: errors,
      }));
      setUi((prev) => ({ ...prev, step: 'complete' }));
    }, 800);
  }, [csv.fieldMap, csv.rows, ui.defaultSource, ui.defaultStage]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setUi((prev) => ({ ...prev, dragOver: false }));
      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setUi((prev) => ({ ...prev, dragOver: true }));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setUi((prev) => ({ ...prev, dragOver: false }));
  }, []);

  const progressPct = useMemo(
    () => (progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0),
    [progress.total, progress.current]
  );

  const setCsvText = useCallback((text: string) => {
    setCsv((prev) => ({ ...prev, text }));
  }, []);

  const setDefaultSource = useCallback((src: string) => {
    setUi((prev) => ({ ...prev, defaultSource: src }));
  }, []);

  const setDefaultStage = useCallback((st: string) => {
    setUi((prev) => ({ ...prev, defaultStage: st }));
  }, []);

  return {
    ui,
    csv,
    progress,
    fileInputRef,
    reset,
    handleFileSelect,
    parseCSV,
    updateFieldMap,
    startImport,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    progressPct,
    setCsvText,
    setDefaultSource,
    setDefaultStage,
  };
}

export default function ImportPage() {
  const {
    ui,
    csv,
    progress,
    fileInputRef,
    reset,
    handleFileSelect,
    parseCSV,
    updateFieldMap,
    startImport,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    progressPct,
    setCsvText,
    setDefaultSource,
    setDefaultStage,
  } = useImport();

  return (
    <div className={styles.pageShell}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Importar Leads</h1>
      </div>

      <div className={styles.pageBody}>
        {ui.message && (
          <div
            className={`${styles.message} ${
              ui.message.type === 'error' ? styles.messageError : styles.messageSuccess
            }`}
          >
            {ui.message.text}
          </div>
        )}

        {ui.step === 'upload' && (
          <UploadStep
            csvText={csv.text}
            dragOver={ui.dragOver}
            fileInputRef={fileInputRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileSelect={handleFileSelect}
            onTextChange={setCsvText}
            onParse={parseCSV}
          />
        )}

        {ui.step === 'preview' && (
          <PreviewStep
            csvHeaders={csv.headers}
            csvRows={csv.rows}
            fieldMap={csv.fieldMap}
            defaultSource={ui.defaultSource}
            defaultStage={ui.defaultStage}
            onFieldMap={updateFieldMap}
            onSourceChange={setDefaultSource}
            onStageChange={setDefaultStage}
            onBack={reset}
            onImport={startImport}
          />
        )}

        {ui.step === 'progress' && (
          <ProgressStep
            progressCurrent={progress.current}
            progressTotal={progress.total}
            progressPct={progressPct}
            errorList={progress.errorList}
          />
        )}

        {ui.step === 'complete' && (
          <CompleteStep
            summarySuccess={progress.summarySuccess}
            summaryErrors={progress.summaryErrors}
            onReset={reset}
          />
        )}
      </div>
    </div>
  );
}

function UploadStep({
  csvText,
  dragOver,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
  onTextChange,
  onParse,
}: {
  csvText: string;
  dragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTextChange: (text: string) => void;
  onParse: () => void;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>1. Subir CSV</h2>
      </div>
      <div className={styles.cardBody}>
        <div
          className={`${styles.uploadArea} ${dragOver ? styles.dragOver : ''}`}
          role="button"
          tabIndex={0}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName !== 'BUTTON') {
              fileInputRef.current?.click();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <div className={styles.uploadIcon}>📤</div>
          <p className={styles.uploadText}>Arrastra un archivo CSV aquí o haz clic para seleccionar</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className={styles.fileInput}
            onChange={onFileSelect}
          />
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => fileInputRef.current?.click()}
          >
            Seleccionar archivo
          </button>
        </div>

        <div className={styles.divider}>
          <span>o pega el contenido CSV</span>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="csv-paste-area" className={styles.label}>
            Pegar CSV
          </label>
          <textarea
            id="csv-paste-area"
            className={styles.textarea}
            rows={6}
            placeholder="Empresa,Contacto,Email,Sector...\nCongalsa,Juan Pérez,juan@congalsa.es,Conservas..."
            value={csvText}
            onChange={(e) => onTextChange(e.target.value)}
          />
        </div>

        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onParse}>
          Analizar CSV
        </button>
      </div>
    </div>
  );
}

function PreviewStep({
  csvHeaders,
  csvRows,
  fieldMap,
  defaultSource,
  defaultStage,
  onFieldMap,
  onSourceChange,
  onStageChange,
  onBack,
  onImport,
}: {
  csvHeaders: string[];
  csvRows: string[][];
  fieldMap: Record<number, string>;
  defaultSource: string;
  defaultStage: string;
  onFieldMap: (idx: number, value: string) => void;
  onSourceChange: (src: string) => void;
  onStageChange: (st: string) => void;
  onBack: () => void;
  onImport: () => void;
}) {
  const previewRows = useMemo(() => csvRows.slice(0, 10), [csvRows]);
  const maxCols = useMemo(() => Math.min(csvHeaders.length, 8), [csvHeaders.length]);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>2. Mapeo de campos</h2>
        <span className={styles.rowCount}>
          {csvHeaders.length} columnas, {csvRows.length} filas
        </span>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.fieldMappingGrid}>
          <div className={styles.colHeader}>Columna CSV</div>
          <div className={styles.colHeader} />
          <div className={styles.colHeader}>Campo Lead</div>
          {csvHeaders.map((header, idx) => (
            <div key={header} className={styles.mappingRow}>
              <div className={styles.fieldLabel}>{header}</div>
              <div className={styles.arrow}>&rarr;</div>
              <select
                className={styles.select}
                value={fieldMap[idx] || ''}
                onChange={(e) => onFieldMap(idx, e.target.value)}
              >
                {LEAD_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className={styles.defaultsSection}>
          <h3 className={styles.defaultsTitle}>Valores por defecto</h3>
          <div className={styles.defaultsGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="default-source" className={styles.label}>
                Fuente
              </label>
              <select
                id="default-source"
                className={styles.select}
                value={defaultSource}
                onChange={(e) => onSourceChange(e.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="outbound_research">Outbound Research</option>
                <option value="inbound_web">Inbound Web</option>
                <option value="referral">Referral</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="default-stage" className={styles.label}>
                Stage
              </label>
              <select
                id="default-stage"
                className={styles.select}
                value={defaultStage}
                onChange={(e) => onStageChange(e.target.value)}
              >
                <option value="new">Nuevo</option>
                <option value="researching">Investigando</option>
                <option value="outreach">Outreach</option>
                <option value="conversation">Conversación</option>
                <option value="diagnostic">Diagnóstico</option>
                <option value="proposal">Propuesta</option>
                <option value="negotiation">Negociación</option>
              </select>
            </div>
          </div>
        </div>

        <h3 className={styles.previewTitle}>Vista previa (primeras 10 filas)</h3>
        <div className={styles.tableWrap}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>#</th>
                {Array.from({ length: maxCols }).map((_, c) => (
                  <th key={c}>{csvHeaders[c]}</th>
                ))}
                {csvHeaders.length > maxCols && <th>...</th>}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rIdx) => (
                <tr key={rIdx}>
                  <td>{rIdx + 1}</td>
                  {Array.from({ length: maxCols }).map((_, c) => (
                    <td key={c} className={styles.previewCell}>
                      {row[c] || ''}
                    </td>
                  ))}
                  {csvHeaders.length > maxCols && (
                    <td className={styles.textSecondary}>+{csvHeaders.length - maxCols}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.actions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onBack}>
            ← Atrás
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onImport}>
            ▶ Importar leads
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressStep({
  progressCurrent,
  progressTotal,
  progressPct,
  errorList,
}: {
  progressCurrent: number;
  progressTotal: number;
  progressPct: number;
  errorList: ImportError[];
}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>3. Importando&hellip;</h2>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
        <div className={styles.progressMeta}>
          <span className={styles.progressText}>
            {progressCurrent} / {progressTotal} procesados
          </span>
          <span className={styles.progressPct}>{progressPct}%</span>
        </div>

        {errorList.length > 0 && (
          <div className={styles.errorsWrap}>
            <h4 className={styles.errorsTitle}>Errores</h4>
            {errorList.slice(0, 20).map((err) => (
              <div key={JSON.stringify(err)} className={styles.errorRow}>
                <span className={styles.errorBadge}>Fila {err.row}</span>
                <span className={styles.errorText}>
                  {err.error}
                  {err.company && ` — ${err.company}`}
                </span>
              </div>
            ))}
            {errorList.length > 20 && (
              <div className={styles.errorsOverflow}>
                ...y {errorList.length - 20} errores más
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CompleteStep({
  summarySuccess,
  summaryErrors,
  onReset,
}: {
  summarySuccess: number;
  summaryErrors: number;
  onReset: () => void;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>✓ Importación completada</h2>
      </div>
      <div className={`${styles.cardBody} ${styles.completeBody}`}>
        <div className={styles.summaryWrap}>
          <div className={`${styles.summaryStat} ${styles.summarySuccess}`}>
            ✓ {summarySuccess} importados
          </div>
          {summaryErrors > 0 && (
            <div className={`${styles.summaryStat} ${styles.summaryError}`}>
              ✗ {summaryErrors} errores
            </div>
          )}
        </div>
        <p className={styles.summaryTotal}>
          Total: {summarySuccess + summaryErrors} filas procesadas
        </p>
        <div className={styles.completeActions}>
          <Link to="/pipeline" className={`${styles.btn} ${styles.btnPrimary}`}>
            Ver en Pipeline
          </Link>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onReset}>
            Importar otro CSV
          </button>
        </div>
      </div>
    </div>
  );
}
